import type {
  GetPlansQuery,
  PlanFeatureInput,
} from '@frameio/graphql-client/generated';
import { useGetPlansQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useIsEnterpriseSubscriber } from '@frameio/shared/hooks';
import { StandardStorageIcon } from '@frameio/shared/icons';
import { EMPTY_ARRAY } from '@frameio/utils';
import { formatBytes } from '@frameio/utils/formatters';
import {
  IconAttachment,
  IconBranding,
  IconBuilding,
  IconCog,
  IconLock,
  IconPeople,
  IconProject,
  IconShare,
  IconShield,
  IconWatermarkId,
  IconWorkspace,
} from '@frameio/vapor-icons';
import { getTierConfig } from '../../../feature-gate/config';
import { FeatureHighlights } from '../../../shared/components/UpgradeDialogLayout';
import TEXT from './generated/TEXT/PlanUpgradeFeatures';
import type { ConversionDialogProps } from './types';

TEXT.DEFINE({
  /** Feature descriptions shown in plan upgrade modal - displayed when user views upgrade options */
  featureDescriptions: {
    /** Enterprise feature - Account-level permission controls */
    adminControls: {
      title: 'Admin Controls',
      description:
        'Control Share permissions at the account level with default settings and the ability to grant Share access to users from specific email domains.',
    },
    /** Pro+ feature - Attach files to comments */
    commentAttachments: {
      title: 'Comment Attachments',
      description:
        'Add image, video, and document attachments to your comments.',
    },
    /** Enterprise feature - Advanced security policies */
    contentSecurity: {
      title: 'Content Security Settings',
      description:
        'Manage Share security, including access types, passphrase, expiration, and more.',
    },
    /** Pro+ feature - Custom branding for share links */
    customBrandedShares: {
      title: 'Custom-Branded Shares',
      description:
        'Upload a custom background image, logo, and description, and display or hide creator info.',
    },
    /** Pro+ feature - Team-only commenting */
    internalComments: {
      title: 'Internal Comments',
      description:
        'Separate internal team discussion from external stakeholder comments in Shares.',
    },
    /** Pro+ feature - Access-controlled projects and folders */
    restrictedProjects: {
      title: 'Restricted Projects & Folders',
      description:
        'Create projects and folders accessible only to you and the people you invite.',
    },
    /** Team feature - Multiple user seats */
    members: {
      title: 'Up to 15 Members',
      description:
        'Add more users to your account for maximum collaboration on projects.',
    },
    /** Enterprise/Team feature - Multiple workspace organization */
    multipleWorkspaces: {
      title: 'Multiple Workspaces',
      description:
        'Create separate Workspaces for each client, department, production, or brand.',
    },
    /** Team feature - Storage that scales with team size */
    projectsAndStorageScaled: {
      title: 'Unlimited Projects & Scaled Storage',
      description: (formattedPerUserStorage: string): string =>
        `Get ${formattedPerUserStorage} more storage for each Member added to your account.`,
    },
    /** Pro feature - Fixed storage allocation */
    projectsAndStorageFixed: {
      title: (formattedActiveStorage: string): string =>
        `Unlimited Projects & ${formattedActiveStorage} Storage`,
      description: 'Increase your storage with higher plan limits.',
    },
    /** Enterprise feature - Organization-level access controls */
    restrictedAccess: {
      title: 'Restricted Access',
      description:
        "Ensure that your sensitive content can only be accessed by people in your organization and users you've added.",
    },
    /** Pro+ feature - Advanced share link security */
    shareLinkSecurity: {
      title: 'Share Link Security & Permissions',
      description:
        'Enable passphrase protection, link expiration, and disable downloads and comments.',
    },
    /** Enterprise feature - SAML SSO integration */
    singleSignOn: {
      title: 'Single Sign-On',
      description:
        'Fast, secure login with your existing SAML 2.0 SSO provider such as Okta, Google, or Microsoft.',
    },
    /** Team feature - Scalable storage with team growth */
    storageScaled: {
      title: (
        formattedActiveStorage: string,
        formattedPerUserStorage: string
      ): string =>
        `${formattedActiveStorage} Storage + ${formattedPerUserStorage} per additional Member`,
      description:
        'Scalable storage that grows with your team. Add a Member, get 2 TB of storage—automatically.',
    },
    /** Pro/Team feature - Fixed storage capacity */
    storageFixed: {
      title: (formattedActiveStorage: string): string =>
        `${formattedActiveStorage} Storage`,
      description:
        'Have even more space to store your media and assets within Frame.io.',
    },
    /** Enterprise feature - Session-based watermarking */
    watermark: {
      title: 'Session-Based Watermarking',
      description:
        "Embed the viewer's email, IP, geolocation, and more to identify the original stream source.",
    },
  },
});

const featureDisplayForPlanTier = (
  plans: NonNullable<GetPlansQuery['account']>['plans']
) => {
  const proPlan = plans?.find((plan) => plan.tier === 'PRO');
  const teamPlan = plans?.find((plan) => plan.tier === 'TEAM');

  const getProjectsAndStorageFeature = (
    perUserStorageIncrement: number,
    activeStorageLimit: number
  ) => {
    const hasScaledStorage = perUserStorageIncrement > 0;
    const formattedActiveStorage = formatBytes(activeStorageLimit);

    if (hasScaledStorage) {
      const formattedPerUserStorage = formatBytes(perUserStorageIncrement);
      return {
        title: TEXT.featureDescriptions.projectsAndStorageScaled.title,
        description:
          TEXT.featureDescriptions.projectsAndStorageScaled.description(
            formattedPerUserStorage
          ),
      };
    }

    return {
      title: TEXT.featureDescriptions.projectsAndStorageFixed.title(
        formattedActiveStorage
      ),
      description: TEXT.featureDescriptions.projectsAndStorageFixed.description,
    };
  };

  const getStorageFeature = (
    perUserStorageIncrement: number,
    activeStorageLimit: number
  ) => {
    const hasScaledStorage = perUserStorageIncrement > 0;
    const formattedActiveStorage = formatBytes(activeStorageLimit);

    if (hasScaledStorage) {
      const formattedPerUserStorage = formatBytes(perUserStorageIncrement);
      return {
        title: TEXT.featureDescriptions.storageScaled.title(
          formattedActiveStorage,
          formattedPerUserStorage
        ),
        description: TEXT.featureDescriptions.storageScaled.description,
      };
    }

    return {
      title: TEXT.featureDescriptions.storageFixed.title(
        formattedActiveStorage
      ),
      description: TEXT.featureDescriptions.storageFixed.description,
    };
  };

  return {
    ENTERPRISE: [
      {
        ...TEXT.featureDescriptions.watermark,
        icon: IconWatermarkId,
      },
      {
        ...TEXT.featureDescriptions.singleSignOn,
        icon: IconBuilding,
      },
      {
        ...TEXT.featureDescriptions.multipleWorkspaces,
        icon: IconWorkspace,
      },
      {
        ...TEXT.featureDescriptions.contentSecurity,
        icon: IconShield,
      },
    ],
    PRO: [
      {
        ...TEXT.featureDescriptions.shareLinkSecurity,
        icon: IconShare,
      },
      {
        ...TEXT.featureDescriptions.customBrandedShares,
        icon: IconBranding,
      },
      {
        ...TEXT.featureDescriptions.commentAttachments,
        icon: IconAttachment,
      },
      {
        ...getProjectsAndStorageFeature(
          proPlan?.perUserStorageIncrement ?? 0,
          proPlan?.activeStorageLimit ?? 0
        ),
        icon: IconProject,
      },
    ],
    TEAM: [
      {
        ...TEXT.featureDescriptions.internalComments,
        icon: IconLock,
      },
      {
        ...TEXT.featureDescriptions.restrictedProjects,
        icon: IconProject,
      },
      {
        ...TEXT.featureDescriptions.members,
        icon: IconPeople,
      },
      {
        ...getStorageFeature(
          teamPlan?.perUserStorageIncrement ?? 0,
          teamPlan?.activeStorageLimit ?? 0
        ),
        icon: StandardStorageIcon,
      },
    ],
    BUSINESS: EMPTY_ARRAY,
    FREE: EMPTY_ARRAY,
    STARTER: EMPTY_ARRAY,
  };
};

const addOnsDisplayForEnterpriseSubscriber = (
  feature: PlanFeatureInput | undefined
) => {
  if (feature === 'SESSION_BASED_WATERMARKING') {
    return [
      {
        ...TEXT.featureDescriptions.watermark,
        icon: IconWatermarkId,
      },
    ];
  }

  return [
    {
      ...TEXT.featureDescriptions.restrictedAccess,
      icon: IconLock,
    },
    {
      ...TEXT.featureDescriptions.adminControls,
      icon: IconCog,
    },
  ];
};

type PlanDetailsProps = {
  feature?: PlanFeatureInput;
} & Pick<ConversionDialogProps, 'tier'>;

export function PlanUpgradeFeatures({
  tier = 'PRO',
  feature,
}: PlanDetailsProps) {
  const accountId = useActiveAccountId() ?? '';
  const isEnterpriseSubscriber = useIsEnterpriseSubscriber();

  const plansQueryResult = useGetPlansQuery({
    variables: { accountId },
    skip: !accountId,
  });

  const plans = plansQueryResult?.data?.account?.plans ?? EMPTY_ARRAY;

  const features = isEnterpriseSubscriber
    ? addOnsDisplayForEnterpriseSubscriber(feature)
    : featureDisplayForPlanTier(plans)[tier];

  const tierConfig = getTierConfig(tier);

  return <FeatureHighlights features={features} iconColor={tierConfig.color} />;
}
